import { useState, useRef, type ReactNode, type FormEvent } from "react";
import { useLocation, useSearch } from "wouter";
import { Button, Card, Label, Input, Textarea } from "@/components/ui";
import { FileText, File as FileIcon, Type, UploadCloud, AlertCircle, Zap, Youtube, Video, Upload, Scissors, Sparkles, CheckCircle, Loader2 } from "lucide-react";
import { useCreateSession, useUploadSessionFile } from "@workspace/api-client-react";
import { useMediaSession, type MediaProgressStep } from "@/hooks/use-media-session";
import { cn } from "@/lib/utils";
import { UpgradeModal } from "@/components/upgrade-modal";

type Tab = "pdf" | "txt" | "text" | "youtube" | "media";

const YOUTUBE_REGEX = /^(https?:\/\/)?(www\.)?(youtube\.com\/(watch\?v=|shorts\/)|youtu\.be\/)[\w-]+/;

const PROGRESS_STEPS: { key: MediaProgressStep; label: string; icon: ReactNode }[] = [
  { key: "uploading",    label: "Uploading...",          icon: <Upload className="w-5 h-5" /> },
  { key: "extracting",   label: "Extracting Audio...",   icon: <Scissors className="w-5 h-5" /> },
  { key: "transcribing", label: "Transcribing with AI...", icon: <Sparkles className="w-5 h-5" /> },
  { key: "finalizing",   label: "Almost ready...",       icon: <CheckCircle className="w-5 h-5" /> },
];

const STEP_ORDER: MediaProgressStep[] = ["uploading", "extracting", "transcribing", "finalizing"];

export default function NewSession() {
  const [, setLocation] = useLocation();
  const search = useSearch();
  const params = new URLSearchParams(search);
  const folderIdParam = params.get("folderId");
  const folderId: number | null = folderIdParam ? parseInt(folderIdParam) : null;

  const [activeTab, setActiveTab] = useState<Tab>("pdf");
  const [title, setTitle] = useState("");
  const [text, setText] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [youtubeUrl, setYoutubeUrl] = useState("");
  const [youtubeUrlError, setYoutubeUrlError] = useState<string | null>(null);
  const [fileSizeError, setFileSizeError] = useState<string | null>(null);
  const [limitError, setLimitError] = useState<string | null>(null);
  const [showUpgradeButton, setShowUpgradeButton] = useState(false);
  const [showUpgrade, setShowUpgrade] = useState(false);

  const handleLimitError = (err: any) => {
    const body = err?.data;
    if (body?.error === "Limit reached") {
      setLimitError(body.message);
      setShowUpgradeButton(true);
      return;
    }
    setLimitError(err?.message || "An error occurred");
    setShowUpgradeButton(false);
  };

  const { mutate: createText, isPending: isTextPending } = useCreateSession({
    mutation: {
      onSuccess: (data) => setLocation(`/sessions/${data.id}`),
      onError: handleLimitError,
    }
  });

  const { mutate: uploadFile, isPending: isUploadPending } = useUploadSessionFile({
    mutation: {
      onSuccess: (data) => setLocation(`/sessions/${data.id}`),
      onError: handleLimitError,
    }
  });

  const {
    isProcessing,
    currentStep,
    error: mediaError,
    limitType,
    submitYouTube,
    submitMedia,
  } = useMediaSession((sessionId) => setLocation(`/sessions/${sessionId}`));

  // Surface media errors into the shared limitError state (via useEffect to avoid render-time setState)
  const prevMediaError = useRef<string | null>(null);
  if (mediaError && mediaError !== prevMediaError.current) {
    prevMediaError.current = mediaError;
    setTimeout(() => {
      setLimitError(mediaError);
      setShowUpgradeButton(!!limitType);
    }, 0);
  }
  if (!mediaError && prevMediaError.current) {
    prevMediaError.current = null;
  }

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();

    if (activeTab === "text") {
      if (!title || !text) return;
      createText({ data: { title, text, ...(folderId != null ? { folderId } : {}) } as any });
    } else if (activeTab === "youtube") {
      if (!YOUTUBE_REGEX.test(youtubeUrl)) {
        setYoutubeUrlError("Please enter a valid YouTube URL");
        return;
      }
      setYoutubeUrlError(null);
      setLimitError(null);
      submitYouTube(youtubeUrl, title, folderId);
    } else if (activeTab === "media") {
      if (!file) return;
      setLimitError(null);
      submitMedia(file, title || file.name.split(".")[0], folderId);
    } else {
      if (!file) return;
      uploadFile({ data: { file: file, title: title || file.name.split('.')[0], ...(folderId != null ? { folderId: String(folderId) } : {}) } as any });
    }
  };

  const isPending = isTextPending || isUploadPending;

  const handleTabSwitch = (id: Tab) => {
    setActiveTab(id);
    setFile(null);
    setText("");
    setYoutubeUrl("");
    setYoutubeUrlError(null);
    setFileSizeError(null);
    setLimitError(null);
    setShowUpgradeButton(false);
  };

  const handleMediaFileChange = (selectedFile: File) => {
    if (selectedFile.size > 500 * 1024 * 1024) {
      setFileSizeError("File exceeds the 500MB limit. Please choose a smaller file.");
      setFile(null);
      return;
    }
    setFileSizeError(null);
    setFile(selectedFile);
  };

  const currentStepIndex = currentStep ? STEP_ORDER.indexOf(currentStep) : -1;

  return (
    <div className="max-w-3xl mx-auto space-y-8 animate-in slide-in-from-bottom-4 duration-500">
      <div>
        <h1 className="text-3xl font-bold font-display">Create Study Session</h1>
        <p className="text-muted-foreground mt-1">Upload material to generate flashcards, summaries, and quizzes.</p>
      </div>

      <Card className="p-6 sm:p-8">
        {/* Tabs */}
        <div className="flex flex-wrap p-1 bg-muted rounded-xl mb-8 gap-1">
          {[
            { id: "pdf",     label: "Upload PDF",    icon: FileIcon },
            { id: "txt",     label: "Upload TXT",    icon: FileText },
            { id: "text",    label: "Paste Text",    icon: Type },
            { id: "youtube", label: "YouTube URL",   icon: Youtube },
            { id: "media",   label: "Upload Media",  icon: Video },
          ].map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => handleTabSwitch(tab.id as Tab)}
                className={cn(
                  "flex-1 flex items-center justify-center gap-2 py-3 text-sm font-semibold rounded-lg transition-all min-w-[80px]",
                  isActive ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"
                )}
              >
                <Icon className="w-4 h-4" />
                <span className="hidden sm:inline">{tab.label}</span>
              </button>
            );
          })}
        </div>

        {/* Progress overlay — shown while media is processing */}
        {isProcessing ? (
          <div className="space-y-6 py-4">
            <p className="text-center text-muted-foreground text-sm">This may take a minute. Please keep this page open.</p>
            <div className="space-y-4">
              {PROGRESS_STEPS.map((step, index) => {
                const isDone = currentStepIndex > index;
                const isActive = currentStepIndex === index;
                const isPendingStep = currentStepIndex < index;
                return (
                  <div key={step.key} className={cn(
                    "flex items-center gap-4 p-4 rounded-xl border transition-all",
                    isActive ? "border-primary/40 bg-primary/5 text-foreground" : isDone ? "border-green-500/30 bg-green-500/5 text-green-600 dark:text-green-400" : "border-border text-muted-foreground"
                  )}>
                    <div className={cn(
                      "w-8 h-8 rounded-full flex items-center justify-center shrink-0",
                      isActive ? "bg-primary/10 text-primary" : isDone ? "bg-green-500/10 text-green-600 dark:text-green-400" : "bg-muted"
                    )}>
                      {isActive ? <Loader2 className="w-5 h-5 animate-spin" /> : isDone ? <CheckCircle className="w-5 h-5" /> : step.icon}
                    </div>
                    <span className={cn("font-medium", isPendingStep && "opacity-50")}>{step.label}</span>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="title">Session Title</Label>
              <Input
                id="title"
                placeholder={file ? file.name.split('.')[0] : "e.g., Chapter 4: Cell Biology"}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required={activeTab === "text"}
              />
            </div>

            {activeTab === "text" && (
              <div className="space-y-2">
                <Label htmlFor="text">Study Material Text</Label>
                <Textarea
                  id="text"
                  placeholder="Paste your notes or text here..."
                  className="min-h-[300px]"
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  required
                />
              </div>
            )}

            {activeTab === "youtube" && (
              <div className="space-y-2">
                <Label htmlFor="youtube-url">YouTube URL</Label>
                <Input
                  id="youtube-url"
                  type="url"
                  placeholder="https://www.youtube.com/watch?v=..."
                  value={youtubeUrl}
                  onChange={(e) => { setYoutubeUrl(e.target.value); setYoutubeUrlError(null); }}
                  onBlur={() => {
                    if (youtubeUrl && !YOUTUBE_REGEX.test(youtubeUrl)) {
                      setYoutubeUrlError("Please enter a valid YouTube URL");
                    }
                  }}
                />
                {youtubeUrlError && (
                  <p className="text-sm text-destructive flex items-center gap-1.5">
                    <AlertCircle className="w-4 h-4 shrink-0" /> {youtubeUrlError}
                  </p>
                )}
                <p className="text-xs text-muted-foreground">Supports youtube.com and youtu.be links. Max 3 hours.</p>
              </div>
            )}

            {(activeTab === "pdf" || activeTab === "txt") && (
              <div className="space-y-2">
                <Label>File Upload ({activeTab.toUpperCase()})</Label>
                <div className="mt-2 flex justify-center rounded-2xl border-2 border-dashed border-border px-6 py-16 hover:border-primary/50 hover:bg-primary/5 transition-colors cursor-pointer"
                     onClick={() => document.getElementById('file-upload')?.click()}>
                  <div className="text-center">
                    <UploadCloud className="mx-auto h-12 w-12 text-muted-foreground" aria-hidden="true" />
                    <div className="mt-4 flex text-sm leading-6 text-muted-foreground justify-center">
                      <label
                        htmlFor="file-upload"
                        className="relative cursor-pointer rounded-md bg-transparent font-semibold text-primary focus-within:outline-none hover:text-primary/80"
                      >
                        <span>Upload a file</span>
                        <input
                          id="file-upload"
                          name="file-upload"
                          type="file"
                          className="sr-only"
                          accept={activeTab === "pdf" ? ".pdf" : ".txt"}
                          onChange={(e) => e.target.files && setFile(e.target.files[0])}
                        />
                      </label>
                      <p className="pl-1">or drag and drop</p>
                    </div>
                    <p className="text-xs leading-5 text-muted-foreground mt-2">
                      {file ? <span className="text-foreground font-medium">{file.name}</span> : `${activeTab.toUpperCase()} up to 10MB`}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {activeTab === "media" && (
              <div className="space-y-2">
                <Label>Video or Audio File</Label>
                <div className="mt-2 flex justify-center rounded-2xl border-2 border-dashed border-border px-6 py-16 hover:border-primary/50 hover:bg-primary/5 transition-colors cursor-pointer"
                     onClick={() => document.getElementById('media-upload')?.click()}>
                  <div className="text-center">
                    <UploadCloud className="mx-auto h-12 w-12 text-muted-foreground" aria-hidden="true" />
                    <div className="mt-4 flex text-sm leading-6 text-muted-foreground justify-center">
                      <label
                        htmlFor="media-upload"
                        className="relative cursor-pointer rounded-md bg-transparent font-semibold text-primary focus-within:outline-none hover:text-primary/80"
                      >
                        <span>Upload a file</span>
                        <input
                          id="media-upload"
                          name="media-upload"
                          type="file"
                          className="sr-only"
                          accept=".mp4,.mp3,.m4a,.wav"
                          onChange={(e) => e.target.files && handleMediaFileChange(e.target.files[0])}
                        />
                      </label>
                      <p className="pl-1">or drag and drop</p>
                    </div>
                    <p className="text-xs leading-5 text-muted-foreground mt-2">
                      {file ? <span className="text-foreground font-medium">{file.name}</span> : "MP4, MP3, M4A, WAV up to 500MB · Max 3 hours"}
                    </p>
                  </div>
                </div>
                {fileSizeError && (
                  <p className="text-sm text-destructive flex items-center gap-1.5">
                    <AlertCircle className="w-4 h-4 shrink-0" /> {fileSizeError}
                  </p>
                )}
              </div>
            )}

            {limitError && (
              <div className="flex items-start gap-3 p-4 rounded-xl bg-destructive/10 border border-destructive/20 text-destructive">
                <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium">{limitError}</p>
                  {showUpgradeButton && (
                    <button
                      type="button"
                      onClick={() => { setLimitError(null); setShowUpgradeButton(false); setShowUpgrade(true); }}
                      className="mt-2 inline-flex items-center gap-1 text-sm font-semibold text-primary hover:underline"
                    >
                      <Zap className="w-4 h-4" /> Upgrade your plan
                    </button>
                  )}
                </div>
              </div>
            )}

            <div className="pt-4 flex justify-end">
              <Button
                type="submit"
                size="lg"
                isLoading={isPending}
                disabled={
                  (activeTab !== "text" && activeTab !== "youtube" && !file) ||
                  (activeTab === "youtube" && !youtubeUrl.trim()) ||
                  !!fileSizeError
                }
              >
                Create Session
              </Button>
            </div>
          </form>
        )}
      </Card>

      <UpgradeModal open={showUpgrade} onClose={() => setShowUpgrade(false)} />
    </div>
  );
}
